/**
 * Shared CodeMirror configuration for editable artifact/code-block surfaces.
 *
 * Centralises the ESLint instance, JSX/HTML/JSON/Markdown language extensions,
 * and the language → extensions mapping used by both the standard chat
 * `<CodeBlock>` and Nebula `<NebulaCanvas>` editors.
 *
 * Why one module: previously each surface created its own `Linter`, its own
 * flat config, and its own `cmExt`/`cmExtension` switch — which drifted apart
 * (e.g. JSON linting was wired in CodeBlock but not Nebula).
 */

import { javascript, esLint } from "@codemirror/lang-javascript";
import { html } from "@codemirror/lang-html";
import { json, jsonParseLinter } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { languages } from "@codemirror/language-data";
import { codeFolding, foldGutter, LanguageDescription } from "@codemirror/language";
import { linter, lintGutter, forEachDiagnostic } from "@codemirror/lint";
import { EditorView, keymap } from "@codemirror/view";
import { search, searchKeymap } from "@codemirror/search";
import { Linter } from "eslint-linter-browserify";
import { PHOTON_FENCE_ALIASES } from "./nebulaArtifactTypes.js";

// Resolves a markdown fenced code block's info string (e.g. "react-viz") to a
// CodeMirror LanguageDescription so fenced blocks get syntax highlighting.
// Falls back to matching by name for any standard language (e.g. "python").
const resolveFenceLanguage = (info) => {
  const name = PHOTON_FENCE_ALIASES[info] ?? info;
  return LanguageDescription.matchLanguageName(languages, name, false);
};

// Photon-themed search panel — light & dark variants
function buildSearchPanelTheme(isDark) {
  const c = {
    barBg: "var(--photon-chat-surface-muted)",
    barBorder: "var(--photon-chat-border)",
    inputBg: "var(--photon-chat-surface)",
    inputBorder: "var(--photon-chat-border)",
    inputText: "var(--photon-chat-text)",
    placeholder: "var(--photon-chat-muted)",
    btnBg: "var(--photon-chat-surface)",
    btnBorder: "var(--photon-chat-border)",
    btnText: "var(--photon-chat-text)",
    closeText: "var(--photon-chat-muted)",
    closeHoverBg: "var(--photon-chat-surface)",
    closeHoverText: "var(--photon-chat-text)",
    labelText: "var(--photon-chat-muted)",
    labelHoverBg: "var(--photon-chat-surface)",
    labelHoverText: "var(--photon-chat-text)",
    cbBorder: "var(--photon-chat-border)",
    cbBg: "var(--photon-chat-surface)",
  };

  return EditorView.theme({
    ".cm-panels-top": {
      borderBottom: `1px solid ${c.barBorder}`,
      background: c.barBg,
      boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.04)",
    },
    ".cm-search": {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "6px",
      padding: "8px 12px",
      background: c.barBg,
      fontFamily: "Inter, sans-serif",
      fontSize: "12px",
      position: "relative",
    },
    ".cm-search br": { display: "none" },
    ".cm-search input": {
      background: c.inputBg,
      color: c.inputText,
      border: `1px solid ${c.inputBorder}`,
      borderRadius: "8px",
      padding: "4px 10px",
      fontSize: "12px",
      outline: "none",
      minWidth: "160px",
      transition: "border-color 0.15s, box-shadow 0.15s",
    },
    ".cm-search input::placeholder": { color: c.placeholder },
    ".cm-search input:focus": {
      borderColor: "var(--photon-chat-accent)",
      boxShadow: "0 0 0 3px color-mix(in srgb, var(--photon-chat-accent) 15%, transparent)",
    },
    ".cm-search input[type='text']": {
      background: c.inputBg,
      color: c.inputText,
      border: `1px solid ${c.inputBorder}`,
      borderRadius: "8px",
      padding: "4px 10px",
      fontSize: "12px",
      outline: "none",
      minWidth: "160px",
      transition: "border-color 0.15s, box-shadow 0.15s",
    },
    ".cm-search input[type='text']:focus": {
      borderColor: "var(--photon-chat-accent)",
      boxShadow: "0 0 0 3px color-mix(in srgb, var(--photon-chat-accent) 15%, transparent)",
    },
    ".cm-search button": {
      background: c.btnBg,
      color: c.btnText,
      border: `1px solid ${c.btnBorder}`,
      borderRadius: "8px",
      padding: "4px 12px",
      fontSize: "11px",
      fontFamily: "Inter, sans-serif",
      fontWeight: "500",
      cursor: "pointer",
      whiteSpace: "nowrap",
      transition: "background 0.12s, border-color 0.12s, transform 0.08s",
      lineHeight: "1.6",
    },
    ".cm-search button:hover": {
      background: "rgb(var(--photon-button-primary))",
      borderColor: "rgb(var(--photon-button-primary))",
      color: "rgb(var(--photon-button-on-primary))",
    },
    ".cm-search button:active": { transform: "scale(0.96)" },
    ".cm-search button[name='close']": {
      background: "transparent",
      border: "none",
      color: c.closeText,
      fontSize: "16px",
      fontWeight: "400",
      padding: "2px 6px",
      marginLeft: "auto",
      borderRadius: "6px",
      width: "24px",
      height: "24px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    },
    ".cm-search button[name='close']:hover": {
      background: c.closeHoverBg,
      color: c.closeHoverText,
      borderColor: "transparent",
      transform: "none",
    },
    ".cm-search label": {
      color: c.labelText,
      fontSize: "11px",
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      cursor: "pointer",
      userSelect: "none",
      padding: "3px 8px",
      borderRadius: "6px",
      transition: "background 0.12s, color 0.12s",
    },
    ".cm-search label:hover": {
      background: c.labelHoverBg,
      color: c.labelHoverText,
    },
    ".cm-search input[type='checkbox']": {
      appearance: "none",
      WebkitAppearance: "none",
      width: "13px",
      height: "13px",
      minWidth: "13px",
      border: `1.5px solid ${c.cbBorder}`,
      borderRadius: "3px",
      background: c.cbBg,
      cursor: "pointer",
      position: "relative",
      padding: "0",
      margin: "0",
      transition: "border-color 0.12s, background 0.12s",
    },
    ".cm-search input[type='checkbox']:hover": { borderColor: "var(--photon-chat-accent)" },
    ".cm-search input[type='checkbox']:checked": {
      background: "rgb(var(--photon-button-primary))",
      borderColor: "rgb(var(--photon-button-primary))",
    },
    ".cm-search input[type='checkbox']:checked::after": {
      content: "''",
      position: "absolute",
      left: "3px",
      top: "0px",
      width: "4px",
      height: "8px",
      border: "solid rgb(var(--photon-button-on-primary))",
      borderWidth: "0 2px 2px 0",
      transform: "rotate(45deg)",
    },
    ".cm-searchMatch": {
      backgroundColor: "color-mix(in srgb, var(--photon-chat-accent) 18%, transparent)",
      outline: "1px solid color-mix(in srgb, var(--photon-chat-accent) 50%, transparent)",
      borderRadius: "2px",
    },
    ".cm-searchMatch.cm-searchMatch-selected": {
      backgroundColor: "color-mix(in srgb, var(--photon-chat-accent) 40%, transparent)",
      outline: "1px solid var(--photon-chat-accent)",
    },
  }, { dark: isDark });
}

// Cache built themes so each render doesn't rebuild a new EditorView.theme
const _searchExtCache = { light: null, dark: null };
function getSearchExt(isDark) {
  const key = isDark ? "dark" : "light";
  if (!_searchExtCache[key]) {
    _searchExtCache[key] = [
      search({ top: true }),
      keymap.of(searchKeymap),
      buildSearchPanelTheme(isDark),
    ];
  }
  return _searchExtCache[key];
}

// (search extension is built per-call via getSearchExt(isDark))

// Single Linter instance shared across all editors (creating one is expensive).
export const eslintInstance = new Linter({ configType: "flat" });

export const eslintConfig = [
  {
    languageOptions: {
      ecmaVersion: 2022,
      sourceType: "module",
      parserOptions: { ecmaFeatures: { jsx: true } },
    },
    rules: { "no-undef": "warn", "no-unused-vars": "warn" },
  },
];

/**
 * Returns the CodeMirror extensions array for a given language tag.
 * Accepts both Nebula artifact types and CodeBlock language tags.
 *
 * @param {string} lang  — "jsx" | "react" | "html" | "json" | "plotly" | "mermaid" | "markdown"
 * @param {boolean} wrap — enable line wrapping (default true)
 * @param {boolean} isDark — use dark-mode search panel theme (default false)
 */
export function getCmExtensions(lang, wrap = true, isDark = false) {
  const wrapExt = wrap ? [EditorView.lineWrapping] : [];
  const foldExt = [codeFolding(), foldGutter()];
  const sExt = getSearchExt(isDark);
  switch (lang) {
    case "jsx":
    case "react":
      return [
        javascript({ jsx: true }),
        lintGutter(),
        linter(esLint(eslintInstance, eslintConfig)),
        ...foldExt,
        ...sExt,
        ...wrapExt,
      ];
    case "html":
      return [html(), lintGutter(), ...foldExt, ...sExt, ...wrapExt];
    case "json":
    case "plotly":
      return [json(), lintGutter(), linter(jsonParseLinter()), ...foldExt, ...sExt, ...wrapExt];
    case "mermaid":
    case "markmap":
    case "markdown":
      return [markdown({ codeLanguages: resolveFenceLanguage }), ...foldExt, ...sExt, ...wrapExt];
    default:
      return [...foldExt, ...sExt, ...wrapExt];
  }
}

/**
 * Extracts the full diagnostics list from a CodeMirror EditorState.
 * Returns an array of { message, severity, line, col } objects.
 *   severity: "info" | "warning" | "error"
 *   line, col: 1-based
 *
 * @param {import('@codemirror/state').EditorState} state
 * @returns {{ message: string, severity: string, line: number, col: number }[]}
 */
export function extractDiagnostics(state) {
  const result = [];
  forEachDiagnostic(state, (d) => {
    const lineInfo = state.doc.lineAt(d.from);
    result.push({
      message: d.message,
      severity: d.severity,   // "info" | "warning" | "error"
      line: lineInfo.number,  // 1-based
      col: d.from - lineInfo.from + 1, // 1-based
    });
  });
  return result;
}
